import jwtDecode from "jwt-decode";
import axios from "axios";

import config from "../../config";

// content type
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.baseURL = config.API_URL;

// intercepting to capture errors
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    let message;

    if (error && error.response && error.response.status === 404) {
      // window.location.href = '/not-found';
    } else if (error && error.response && error.response.status === 403) {
      window.location.href = "/access-denied";
    } else {
      switch (error.response.status) {
        case 401:
          message = "Invalid OTP";
          break;
        case 403:
          message = "Access Forbidden";
          break;
        case 404:
          message = "Sorry! the data you are looking for could not be found";
          break;
        default: {
          message =
            error.response && error.response.data
              ? error.response.data["message"]
              : error.message || error;
        }
      }
      return Promise.reject(message);
    }
  }
);

const AUTH_STORAGE_KEY = "watcho_user";

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token: string | null) => {
  if (token) {
    axios.defaults.headers.common["Authorization"] = "JWT " + token;
    localStorage.setItem("authToken", token);
  } else {
    delete axios.defaults.headers.common["Authorization"];
    localStorage.removeItem("authToken");
  }
};

const getUserFromStorage = () => {
  const user = localStorage.getItem(AUTH_STORAGE_KEY);
  return user ? (typeof user == "object" ? user : JSON.parse(user)) : null;
};

// const getKeyFromStorage = (key: string) => {
//   const user = localStorage.getItem(key);
//   return user ? (typeof user == "object" ? user : JSON.parse(user)) : null;
// };

const getKeyFromStorage = (key: string) => {
  try {
    const value = localStorage.getItem(key);
    if (!value) return null;

    // Attempt to parse only if it's a JSON-like string
    if ((value.startsWith("{") || value.startsWith("["))) {
      return JSON.parse(value);
    }

    return value; // Return plain string if not JSON
  } catch (err) {
    console.error(`Error parsing localStorage key "${key}":`, err);
    return null;
  }
};
class APICore {
  /**
   * get api key
   */
  apiKey = () => {
    return config.API_KEY;
  };

  /**
   * get api url
   */
  baseUrl = (url: string) => {
    return config.API_URL + url;
  };
  /**
   * Fetches data from given url
   */
  get = (url: string, params: any) => {
    let response;
    if (params) {
      var queryString = params
        ? Object.keys(params)
            .map((key) => key + "=" + params[key])
            .join("&")
        : "";
      response = axios.get(`${url}?${queryString}`, params);
    } else {
      response = axios.get(`${url}`, params);
    }
    return response;
  };

  getFile = (url: string, params: any) => {
    let response;
    if (params) {
      var queryString = params
        ? Object.keys(params)
            .map((key) => key + "=" + params[key])
            .join("&")
        : "";
      response = axios.get(`${url}?${queryString}`, { responseType: "blob" });
    } else {
      response = axios.get(`${url}`, { responseType: "blob" });
    }
    return response;
  };

  getMultiple = (urls: string, params: any) => {
    const reqs = [];
    let queryString = "";
    if (params) {
      queryString = params
        ? Object.keys(params)
            .map((key) => key + "=" + params[key])
            .join("&")
        : "";
    }

    for (const url of urls) {
      reqs.push(axios.get(`${url}?${queryString}`));
    }
    return axios.all(reqs);
  };

  /**
   * post given data to url
   */
  create = (url: string, data: any) => {
    return axios.post(url, data);
  };

  /**
   * Updates patch data
   */
  updatePatch = (url: string, data: any) => {
    return axios.patch(url, data);
  };

  /**
   * Updates data
   */
  update = (url: string, data: any) => {
    return axios.put(url, data);
  };

  /**
   * Deletes data
   */
  delete = (url: string) => {
    return axios.delete(url);
  };

  /**
   * post given data to url with file
   */
  createWithFile = (url: string, data: any) => {
    const formData = new FormData();
    for (const k in data) {
      formData.append(k, data[k]);
    }

    const config = {
      headers: {
        ...axios.defaults.headers,
        "content-type": "multipart/form-data",
      },
    };
    return axios.post(url, formData, config);
  };

  /**
   * post given data to url with file
   */
  updateWithFile = (url: string, data: any) => {
    const formData = new FormData();
    for (const k in data) {
      formData.append(k, data[k]);
    }

    const config = {
      headers: {
        ...axios.defaults.headers,
        "content-type": "multipart/form-data",
      },
    };
    return axios.patch(url, formData, config);
  };

  isUserAuthenticated = () => {
    const user = this.getLoggedInUser();

    if (!user) {
      return false;
    }
    const decoded: any = jwtDecode(user.authToken);
    const currentTime = Date.now() / 1000;
    if (decoded.exp < currentTime) {
      console.warn("access token expired");
      return false;
    } else {
      return true;
    }
  };

  setLoggedInUser = (session: any) => {
    if (session)
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
    else {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  };
  /**
   * Returns the logged in user
   */
  getLoggedInUser = () => {
    return getUserFromStorage();
  };

  getDataFromStorage = (key: string) => {
    return getKeyFromStorage(key);
  };

  // setDataInStorage = (key: string, value: any) => {
  //   if (!key) return; // Ensure key is valid

  //   if (value !== null && value !== undefined) {
  //     localStorage.setItem(key, JSON.stringify(value));
  //   } else {
  //     localStorage.removeItem(key);
  //   }
  // };

  setDataInStorage = (key: string, value: any) => {
    if (!key) return;
  
    if (value !== null && value !== undefined) {
      const isString = typeof value === 'string';
      localStorage.setItem(key, isString ? value : JSON.stringify(value));
    } else {
      localStorage.removeItem(key);
    }
  };

  setUserInStorage = (modifiedUser: any) => {
    let userInfo = localStorage.getItem(AUTH_STORAGE_KEY);
    if (userInfo) {
      const { authToken, user } = JSON.parse(userInfo);
      this.setLoggedInUser({ authToken, ...user, ...modifiedUser });
    }
  };

  /**
   * Clear all local storage data
   */
  clearStorage = () => {
    localStorage.clear();
  };
}

/*
Check if token available in localStorage
*/
let user = getUserFromStorage();
if (user) {
  const { authToken } = user;
  if (authToken) {
    setAuthorization(authToken);
  }
}

export { APICore, setAuthorization };
